import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarClock, CheckCircle2, Inbox, Send, ChevronRight, RefreshCw } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import StatusBadge from "../../components/StatusBadge";
import DueChip from "../../components/DueChip";
import SendReminderDialog from "../../components/SendReminderDialog";
import { Button } from "../../components/ui/button";
import api, { formatCurrency, formatDate, daysUntil } from "../../lib/api";
import { useTerms } from "../../lib/BrandContext";
import { withReturnTo } from "../../lib/backNavigation";

export default function Charges() {
  const { terms } = useTerms();
  const [charges, setCharges] = useState(null);
  const [reminderCharge, setReminderCharge] = useState(null);
  useEffect(() => {
    api.get("/charges")
      .then((r) => setCharges(r.data))
      .catch(() => setCharges([]));
  }, []);

  if (charges === null) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center text-ink-500" data-testid="charges-loading">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-ink-900 sm:text-4xl">Fees</h1>
          <p className="mt-1 text-base text-ink-600">Dues, uniforms, tournaments — anything {terms.athletes} owe.</p>
        </div>
        <Link to="/coach/charges/new" data-testid="charges-new-button">
          <Button className="h-12 bg-brand font-semibold text-white hover:bg-brand-hover">
            <Plus className="mr-1 h-4 w-4" /> Add Fee
          </Button>
        </Link>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {charges.map((c) => {
          const d = daysUntil(c.due_date);
          const tone = d != null && d < 0 ? "text-rose-600" : d != null && d <= 3 ? "text-amber-700" : "text-ink-700";
          const isCanceled = c.status === "canceled";
          return (
            <div
              key={c.id}
              className={`rounded-xl border border-ink-100 bg-white shadow-sm card-hover overflow-hidden ${isCanceled ? "opacity-70 grayscale-[35%]" : ""}`}
              data-testid={`charge-card-${c.id}`}
              data-canceled={isCanceled ? "true" : "false"}
            >
              <Link
                to={withReturnTo(`/coach/charges/${c.id}`, { from: "/coach/charges", label: "Fees" })}
                className="block p-6 hover:bg-ink-100/30 transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40"
                data-testid={`charge-card-link-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg font-bold text-ink-900">{c.title}</h3>
                    <p className="text-xs text-ink-500 uppercase tracking-wide">{c.type.replace("_", " ")}</p>
                  </div>
                  <div className="flex shrink-0 items-start gap-2">
                    <div className="flex flex-col items-end gap-1.5">
                      <StatusBadge status={c.status} />
                      {c.recurring_monthly ? (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand"
                          data-testid={`charge-recurring-chip-${c.id}`}
                          title="This fee auto-generates monthly"
                        >
                          <RefreshCw className="h-3 w-3" /> Monthly
                        </span>
                      ) : null}
                      <DueChip dueDate={c.due_date} chargeStatus={c.status} />
                    </div>
                    <ChevronRight className="mt-0.5 h-5 w-5 text-ink-400" aria-hidden="true" />
                  </div>
                </div>
                <p className="mt-3 font-heading text-3xl font-bold text-ink-900">{formatCurrency(c.amount)}</p>
                <p className="mt-1 text-sm text-ink-500">Due {formatDate(c.due_date)}</p>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-lg bg-emerald-50 p-2">
                    <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" />
                    <p className="mt-1 font-bold text-emerald-700">{c.confirmed_count}</p>
                    <p className="text-ink-500">Confirmed</p>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-2">
                    <Inbox className="mx-auto h-4 w-4 text-amber-600" />
                    <p className="mt-1 font-bold text-amber-700">{c.submitted_count}</p>
                    <p className="text-ink-500">Submitted</p>
                  </div>
                  <div className="rounded-lg bg-ink-100 p-2">
                    <CalendarClock className="mx-auto h-4 w-4 text-ink-500" />
                    <p className="mt-1 font-bold text-ink-900">{c.total_records}</p>
                    <p className="text-ink-500">Total</p>
                  </div>
                </div>

                <p className={`mt-4 text-sm font-semibold ${tone}`} data-testid={`charge-due-text-${c.id}`}>
                  {d == null ? "—" : d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? "Due today" : `in ${d}d`}
                </p>
              </Link>
              {c.status !== "canceled" ? (
                <div className="border-t border-ink-100 bg-ink-100/30 p-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReminderCharge(c)}
                    className="h-9 w-full border-amber-300 font-semibold text-amber-800 hover:bg-amber-50"
                    data-testid={`charge-remind-${c.id}`}
                  >
                    <Send className="mr-1 h-4 w-4" /> Send reminder
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
        {charges.length === 0 ? (
          <div className="col-span-full rounded-xl border-2 border-dashed border-ink-200 bg-white p-10 text-center" data-testid="charges-empty">
            <CalendarClock className="mx-auto h-8 w-8 text-ink-300" />
            <p className="mt-3 font-semibold text-ink-900">Create your first fee to begin collecting payments.</p>
            <p className="text-sm text-ink-500">Dues, uniforms, tournaments — anything {terms.athletes} owe.</p>
            <Link to="/coach/charges/new" className="mt-4 inline-block" data-testid="charges-empty-cta">
              <Button className="h-11 bg-brand px-5 font-semibold text-white hover:bg-brand-hover">
                <Plus className="mr-1 h-4 w-4" /> Add Fee
              </Button>
            </Link>
          </div>
        ) : null}
      </div>

      <SendReminderDialog
        open={!!reminderCharge}
        onOpenChange={(o) => { if (!o) setReminderCharge(null); }}
        charge={reminderCharge}
      />
    </AppLayout>
  );
}
